/**
 * SoundPlayer - Remotion Audio integration component with timing constraint enforcement
 * This component ensures sound effects respect animation timing boundaries
 */

import React, { useMemo, useCallback } from 'react';
import { Audio, useCurrentFrame, useVideoConfig, Sequence } from 'remotion';
import { 
    calculateVolumeWithFades, 
    shouldSoundBePlaying, 
    calculateSoundFilePosition 
} from './SoundConstraints';
import { 
    SoundPlayerConfig, 
    SoundPlaybackState,
} from './types';
import { SoundConstraints, SoundConfig } from '../types/animated';

/**
 * Props for the SoundPlayer component
 */
export interface SoundPlayerProps {
    config: SoundConfig;           // Sound configuration with metadata
    constraints: SoundConstraints;         // Pre-calculated timing constraints
    itemId: string;                        // Associated animated item ID
    soundType: 'entrance' | 'exit' | 'custom'; // Type of sound effect
    onPlaybackStateChange?: (state: SoundPlaybackState) => void; // State callback
    onError?: (error: Error) => void;      // Error handling callback
    disabled?: boolean;                    // Disable sound playback for testing
}

/**
 * SoundPlayer component - Integrates with Remotion Audio for constraint-aware playback
 */
export const SoundPlayer: React.FC<SoundPlayerProps> = ({
    config,
    constraints,
    itemId,
    soundType,
    onPlaybackStateChange,
    onError,
    disabled = false
}) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const currentTime = frame / fps;

    // Calculate current volume with fade processing (Sequence handles timing)
    const currentVolume = useMemo(() => {
        if (disabled) return 0;
        return calculateVolumeWithFades(currentTime, constraints, config);
    }, [disabled, currentTime, constraints, config]);

    // Calculate position in the original sound file
    const soundFilePosition = useMemo(() => {
        if (disabled) return 0;
        return calculateSoundFilePosition(currentTime, constraints, config);
    }, [disabled, currentTime, constraints, config]);

    // Calculate frame-based timing for Remotion Audio and Sequence
    const audioTiming = useMemo(() => {
        const startFrame = Math.round(constraints.effectiveStart * fps);
        const trimStartFrames = Math.round((config.timing?.trimStart ?? 0) * fps);
        
        // Simple and correct: play for the constraint duration
        const sequenceDuration = Math.round(constraints.maxDuration * fps);
        const audioEndAtFrames = trimStartFrames + sequenceDuration;
        
        const timing = {
            startFrame,
            trimStartFrames,
            sequenceDuration,
            audioEndAtFrames
        };

        // Debug logging for timing verification
        if (process.env.NODE_ENV === 'development') {
            console.log(`[SoundPlayer Debug] ${itemId} (${soundType}):`, {
                effectiveStart: constraints.effectiveStart,
                maxDuration: constraints.maxDuration,
                startFrame: timing.startFrame,
                sequenceDuration: timing.sequenceDuration,
                trimStartFrames: timing.trimStartFrames,
                audioEndAtFrames: timing.audioEndAtFrames
            });
        }

        return timing;
    }, [constraints, config, fps]);

    // Calculate if sound should be playing (for state callback)
    const isPlaying = useMemo(() => {
        return !disabled && shouldSoundBePlaying(currentTime, constraints);
    }, [disabled, currentTime, constraints]);

    // Playback state callback
    const playbackState: SoundPlaybackState = useMemo(() => ({
        isPlaying,
        currentTime,
        volume: currentVolume,
        effectiveStart: constraints.effectiveStart,
        effectiveEnd: constraints.effectiveEnd,
        willBeTrimmed: constraints.willBeTrimmed
    }), [isPlaying, currentTime, currentVolume, constraints]);

    // Notify parent of playback state changes
    React.useEffect(() => {
        if (onPlaybackStateChange) {
            onPlaybackStateChange(playbackState);
        }
    }, [playbackState, onPlaybackStateChange]);

    // Error handling wrapper for Audio component (React event)
    const handleAudioErrorEvent = useCallback((event: React.SyntheticEvent<HTMLAudioElement, Event>) => {
        const error = new Error(`Audio playback error for ${itemId} (${soundType})`);
        console.error(`SoundPlayer error for ${itemId} (${soundType}):`, error);
        if (onError) {
            onError(error);
        }
    }, [itemId, soundType, onError]);

    // Error handling wrapper for general errors
    const handleAudioError = useCallback((error: Error) => {
        console.error(`SoundPlayer error for ${itemId} (${soundType}):`, error);
        if (onError) {
            onError(error);
        }
    }, [itemId, soundType, onError]);

    // Validate constraints before rendering
    if (!constraints.isValid) {
        console.warn(`Invalid sound constraints for ${itemId} (${soundType}), skipping audio`);
        return null;
    }

    // Always render the Sequence, but the Audio inside will only play when appropriate
    try {
        return (
            <Sequence
                from={audioTiming.startFrame}
                durationInFrames={audioTiming.sequenceDuration}
                name={`${soundType}-sequence-${itemId}`}
            >
                <Audio
                    src={config.sound}
                    startFrom={audioTiming.trimStartFrames}
                    endAt={audioTiming.audioEndAtFrames}
                    volume={currentVolume}
                    name={`${soundType}-sound-${itemId}`}
                    onError={handleAudioErrorEvent}
                    // Remotion Audio props for performance and no looping
                    crossOrigin="anonymous"
                    preload="auto"
                    loop={false}  // Prevent looping
                />
            </Sequence>
        );
    } catch (error) {
        handleAudioError(error as Error);
        return null;
    }
};

/**
 * Enhanced SoundPlayer with validation and debugging
 */
export interface EnhancedSoundPlayerProps extends SoundPlayerProps {
    enableDebugLogging?: boolean;         // Enable detailed debug logging
    enableValidation?: boolean;           // Enable runtime validation
    fallbackBehavior?: 'silent' | 'placeholder'; // Behavior when sound fails
}

export const EnhancedSoundPlayer: React.FC<EnhancedSoundPlayerProps> = ({
    enableDebugLogging = false,
    enableValidation = true,
    fallbackBehavior = 'silent',
    ...props
}) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const currentTime = frame / fps;

    // Debug logging
    React.useEffect(() => {
        if (enableDebugLogging) {
            console.log(`[SoundPlayer Debug] ${props.itemId} (${props.soundType})`, {
                frame,
                currentTime: currentTime.toFixed(3),
                isPlaying: shouldSoundBePlaying(currentTime, props.constraints),
                volume: calculateVolumeWithFades(currentTime, props.constraints, props.config).toFixed(3),
                constraints: props.constraints
            });
        }
    }, [frame, currentTime, props, enableDebugLogging]);

    // Runtime validation
    const validationError = useMemo(() => {
        if (!enableValidation) return null;

        if (!props.config.sound) {
            return new Error(`No sound file specified for ${props.itemId}`);
        }

        if (!props.constraints.isValid) {
            return new Error(`Invalid timing constraints for ${props.itemId}`);
        }

        if (props.constraints.maxDuration <= 0) {
            return new Error(`Zero or negative duration for ${props.itemId}`);
        }

        return null;
    }, [enableValidation, props]);

    // Handle validation errors
    if (validationError) {
        console.error('SoundPlayer validation failed:', validationError);
        
        if (props.onError) {
            props.onError(validationError);
        }

        if (fallbackBehavior === 'silent') {
            return null;
        }

        // Placeholder behavior could show visual indicator
        return (
            <div style={{ display: 'none' }} data-sound-error={validationError.message} />
        );
    }

    return <SoundPlayer {...props} />;
};

/**
 * Multi-sound player for handling multiple simultaneous sounds
 */
export interface MultiSoundPlayerProps {
    sounds: Array<SoundPlayerProps>;      // Array of sound configurations
    maxConcurrentSounds?: number;         // Limit concurrent sounds for performance
    priorityOrder?: ('entrance' | 'exit' | 'custom')[]; // Priority when limiting sounds
}

export const MultiSoundPlayer: React.FC<MultiSoundPlayerProps> = ({
    sounds,
    maxConcurrentSounds = 8,
    priorityOrder = ['entrance', 'exit', 'custom']
}) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const currentTime = frame / fps;

    // Filter and prioritize sounds that should be playing
    const activeSounds = useMemo(() => {
        const playing = sounds.filter(sound => 
            shouldSoundBePlaying(currentTime, sound.constraints) && !sound.disabled
        );

        // If we exceed the limit, prioritize by type
        if (playing.length > maxConcurrentSounds) {
            const prioritized = [...playing].sort((a, b) => {
                const aPriority = priorityOrder.indexOf(a.soundType);
                const bPriority = priorityOrder.indexOf(b.soundType);
                return aPriority - bPriority;
            });

            return prioritized.slice(0, maxConcurrentSounds);
        }

        return playing;
    }, [sounds, currentTime, maxConcurrentSounds, priorityOrder]);

    return (
        <>
            {activeSounds.map((soundProps, index) => (
                <SoundPlayer
                    key={`${soundProps.itemId}-${soundProps.soundType}-${index}`}
                    {...soundProps}
                />
            ))}
        </>
    );
};

/**
 * Utility hook for sound player state management
 */
export const useSoundPlayer = (
    config: SoundConfig,
    constraints: SoundConstraints,
    itemId: string,
    soundType: 'entrance' | 'exit' | 'custom'
) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const currentTime = frame / fps;

    const isPlaying = shouldSoundBePlaying(currentTime, constraints);
    const volume = calculateVolumeWithFades(currentTime, constraints, config);
    const soundFilePosition = calculateSoundFilePosition(currentTime, constraints, config);

    const playbackState: SoundPlaybackState = {
        isPlaying,
        currentTime,
        volume,
        effectiveStart: constraints.effectiveStart,
        effectiveEnd: constraints.effectiveEnd,
        willBeTrimmed: constraints.willBeTrimmed
    };

    return {
        playbackState,
        isPlaying,
        volume,
        soundFilePosition,
        constraints
    };
};

export default SoundPlayer;